import { useState } from 'react';
import clsx from 'clsx';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api, UnauthorizedError } from '@/api/client';
import type { SuperSearchStatus, UpdateSettings } from '@/api/types';
import { AtoSessionBadge } from '@/components/AtoSessionBadge';
import { RunDetail } from '@/components/RunDetail';
import { RunsTable } from '@/components/RunsTable';
import { SettingsPanel } from '@/components/SettingsPanel';
import { SignIn } from '@/components/SignIn';

type Tab = 'all' | 'attention' | 'waiting' | 'succeeded' | 'settings';

const WAITING: SuperSearchStatus[] = [
  'AtoNotAuthenticated',
  'AtoUnavailable',
  'QueuedAtAto',
  'AwaitingLodgement',
];

/**
 * Auth gate. Everything below only renders once /users/me answers, so no screen ever flashes
 * data it then has to take away.
 */
export function App() {
  const me = useQuery({
    queryKey: ['me'],
    queryFn: api.me,
    retry: false,
    staleTime: 5 * 60_000,
  });

  if (me.isPending) {
    return <div className="flex min-h-screen items-center justify-center text-sm text-zinc-500">Loading…</div>;
  }

  if (me.error) return <SignIn />;

  return <Console displayName={me.data?.displayName ?? me.data?.email ?? ''} />;
}

function Console({ displayName }: { displayName: string }) {
  const queryClient = useQueryClient();
  const [tab, setTab] = useState<Tab>('all');
  const [search, setSearch] = useState('');
  const [selectedId, setSelectedId] = useState<number | null>(null);
  // Lives here, not inside SettingsPanel: the panel unmounts when you switch tabs, and
  // half-entered credentials shouldn't die with it.
  const [settingsDraft, setSettingsDraft] = useState<UpdateSettings | null>(null);

  const runs = useQuery({
    queryKey: ['runs', tab, search],
    queryFn: () =>
      api.listRuns({
        needsAttention: tab === 'attention',
        status: tab === 'succeeded' ? 'Succeeded' : undefined,
        search: search || undefined,
      }),
    enabled: tab !== 'settings',
    // The queue moves on its own — the table should too, without anyone reaching for F5.
    refetchInterval: 15_000,
  });

  const retry = useMutation({
    mutationFn: api.retryRun,
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['runs'] }),
  });

  const signOut = useMutation({
    mutationFn: api.logout,
    // resetQueries, not clear: it refetches the mounted `me` query, so the drop back to the
    // sign-in screen is immediate rather than waiting for the next poll to hit a 401.
    onSuccess: () => queryClient.resetQueries(),
  });

  // A cookie that expired mid-session drops straight back to the sign-in screen.
  if (runs.error instanceof UnauthorizedError) return <SignIn />;

  const counts = runs.data?.counts;
  const visible =
    tab === 'waiting'
      ? (runs.data?.runs ?? []).filter((run) => WAITING.includes(run.status))
      : (runs.data?.runs ?? []);

  const tabs: [Tab, string, number | undefined][] = [
    ['all', 'All runs', counts?.total],
    ['attention', 'Needs attention', counts?.needsAttention],
    ['waiting', 'Waiting on ATO', counts?.waiting],
    ['succeeded', 'Synced', counts?.succeeded],
    ['settings', 'Settings', undefined],
  ];

  return (
    <div className="min-h-screen">
      <header className="border-b border-zinc-800 bg-zinc-900/40">
        <div className="mx-auto flex max-w-[1600px] flex-wrap items-center justify-between gap-4 px-4 py-4 sm:px-6">
          <div className="flex items-baseline gap-3">
            <h1 className="text-lg font-semibold tracking-tight">
              SUPER<span className="text-emerald-400">TRON</span>
            </h1>
            <p className="text-xs text-zinc-500">Signature → ATO super search → Ontraport</p>
          </div>

          <div className="flex items-center gap-4">
            <AtoSessionBadge />
            <span className="text-xs text-zinc-500">{displayName}</span>
            <button className="btn px-2 py-1 text-xs" onClick={() => signOut.mutate()} disabled={signOut.isPending}>
              Sign out
            </button>
          </div>
        </div>

        <nav className="mx-auto flex max-w-[1600px] gap-1 overflow-x-auto px-4">
          {tabs.map(([key, label, count]) => (
            <button
              key={key}
              onClick={() => setTab(key)}
              className={clsx(
                'shrink-0 border-b-2 px-3 py-2 text-sm whitespace-nowrap transition-colors',
                tab === key
                  ? 'border-emerald-400 text-zinc-100'
                  : 'border-transparent text-zinc-500 hover:text-zinc-300',
              )}
            >
              {label}
              {count !== undefined && count > 0 && (
                <span className="ml-1.5 rounded-full bg-zinc-800 px-1.5 py-0.5 font-mono text-[11px] text-zinc-400">
                  {count}
                </span>
              )}
            </button>
          ))}
        </nav>
      </header>

      <main className="mx-auto max-w-[1600px] px-4 py-6 sm:px-6">
        {tab === 'settings' ? (
          <SettingsPanel draft={settingsDraft} onDraftChange={setSettingsDraft} />
        ) : (
          <>
            <div className="mb-4 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between sm:gap-4">
              <input
                className="field-input w-full font-sans sm:max-w-xs"
                placeholder="Search name or email…"
                value={search}
                onChange={(event) => setSearch(event.target.value)}
              />
              <ManualRun />
            </div>

            <div className={clsx('grid gap-5', selectedId ? 'lg:grid-cols-[1fr_28rem]' : 'grid-cols-1')}>
              <RunsTable
                runs={visible}
                selectedId={selectedId}
                onSelect={setSelectedId}
                onRetry={(id) => retry.mutate(id)}
                retryingId={retry.isPending ? (retry.variables ?? null) : null}
              />
              {selectedId && <RunDetail runId={selectedId} onClose={() => setSelectedId(null)} />}
            </div>
          </>
        )}
      </main>
    </div>
  );
}

/** Run a search for one client without waiting for them to sign again. */
function ManualRun() {
  const queryClient = useQueryClient();
  const [email, setEmail] = useState('');

  const create = useMutation({
    mutationFn: () => api.createRun(email.trim()),
    onSuccess: () => {
      setEmail('');
      queryClient.invalidateQueries({ queryKey: ['runs'] });
    },
  });

  return (
    <div className="flex w-full items-center gap-2 sm:w-auto">
      <input
        className="field-input w-full font-sans sm:w-64"
        placeholder="client@example.com"
        value={email}
        onChange={(event) => setEmail(event.target.value)}
      />
      <button
        className="btn btn-primary whitespace-nowrap"
        disabled={!email.trim() || create.isPending}
        onClick={() => create.mutate()}
      >
        {create.isPending ? 'Queueing…' : 'Run search'}
      </button>
    </div>
  );
}
